import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getHrAdminDashboard } from '../../services/hr_dashboard';
import { toast } from 'react-toastify';
import Breadcrumbs from '../../components/BreadCrumbs';
import DateRangePicker from '../../components/inputFields/DateRangePicker';

/* ═══════════════════════════ Font Tokens ═══════════════════════════ */
const FONT = {
    display: "'Fraunces', Georgia, serif",
    ui: "'Inter', -apple-system, sans-serif",
    mono: "'JetBrains Mono', 'Menlo', monospace",
};

/* ═══════════════════════════ Colors ═══════════════════════════ */
const C = {
    bg: '#f7f6f3',
    card: '#ffffff',
    border: '#e8e7e4',
    textPrimary: '#1a1a1a',
    textSecondary: '#6b7280',
    textMuted: '#9ca3af',
    accent: '#3d5a3e',
    accentLight: '#e8efe8',
    success: '#22c55e',
    warning: '#f59e0b',
    danger: '#ef4444',
    shiftBar: '#3d5a3e',
    shiftBarLive: '#4a7a4e',
    absent: '#f1f0ed',
};

/* ═══════════════════════════ Helpers ═══════════════════════════ */
const todayStr = () => new Date().toISOString().slice(0, 10);

const getMonday = (d) => {
    const dt = new Date(d);
    const day = dt.getDay();
    const diff = dt.getDate() - day + (day === 0 ? -6 : 1);
    dt.setDate(diff);
    return dt.toISOString().slice(0, 10);
};
const getMonthStart = (d) => {
    const dt = new Date(d);
    return new Date(dt.getFullYear(), dt.getMonth(), 1).toISOString().slice(0, 10);
};
const getYesterday = () => {
    const dt = new Date();
    dt.setDate(dt.getDate() - 1);
    return dt.toISOString().slice(0, 10);
};

const parseTime = (val) => {
    if (!val) return null;
    const s = String(val);
    const parts = s.split(':');
    if (parts.length >= 2) return { h: Number(parts[0]), m: Number(parts[1]), s: parts[2] ? Number(parts[2]) : 0 };
    return null;
};

const fmtTimeFull = (val) => {
    if (!val) return '—';
    const s = String(val);
    const parts = s.split(':');
    if (parts.length >= 2) return `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}:${(parts[2] || '00').padStart(2, '0')}`;
    return s;
};

const fmtTimeShort = (val) => {
    if (!val) return '—';
    const s = String(val);
    const parts = s.split(':');
    if (parts.length >= 2) return `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}`;
    return s;
};

const fmtTalkTime = (secs) => {
    if (!secs || secs === 0) return '0m';
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

const fmtWorkingHours = (val) => {
    if (!val) return '0h 0m';
    if (typeof val === 'string' && val.includes(':')) {
        const [h, m] = val.split(':').map(Number);
        return `${h}h ${m}m`;
    }
    if (typeof val === 'number' && isFinite(val)) {
        const h = Math.floor(val);
        const m = Math.round((val - h) * 60);
        return `${h}h ${m}m`;
    }
    return String(val);
};

const workingHoursToMinutes = (val) => {
    if (!val) return 0;
    if (typeof val === 'string' && val.includes(':')) {
        const [h, m] = val.split(':').map(Number);
        return h * 60 + m;
    }
    if (typeof val === 'number') return Math.round(val * 60);
    return 0;
};

const minutesToDisplay = (totalMin) => {
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    return `${h}h ${m}m`;
};

const fmtDate = (d) => {
    if (!d) return '—';
    const dt = new Date(d);
    return isNaN(dt) ? d : dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const getInitials = (name) => {
    if (!name) return '?';
    const words = name.trim().split(/\s+/);
    if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
    return name.slice(0, 2).toUpperCase();
};

const AVATAR_COLORS = [
    '#3d5a3e', '#5b4a9e', '#c2703e', '#2d7d9a', '#8b4562',
    '#4a6741', '#7c5c3c', '#3a6b8c', '#6b4c8a', '#8c6d3f',
];
const getAvatarColor = (name) => {
    let hash = 0;
    for (let i = 0; i < (name || '').length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

/* ═══════════════════════════ Shift Timeline Constants ═══════════════════════════ */
const SHIFT_START = 9; // 09:00
const SHIFT_END = 21;  // 21:00
const SHIFT_HOURS = SHIFT_END - SHIFT_START; // 12 hours

const timeToShiftPct = (h, m = 0) => {
    const fractional = h + m / 60;
    return Math.max(0, Math.min(100, ((fractional - SHIFT_START) / SHIFT_HOURS) * 100));
};

/* ═══════════════════════════ KPI Tile ═══════════════════════════ */
const KpiTile = ({ label, value, sub, isPrimary = false }) => (
    <div style={{
        background: isPrimary ? C.accent : C.card,
        color: isPrimary ? '#fff' : C.textPrimary,
        borderRadius: 14,
        border: isPrimary ? 'none' : `1px solid ${C.border}`,
        padding: '18px 22px',
        flex: 1,
        minWidth: 140,
    }}>
        <div style={{ fontSize: 10, fontWeight: 700, fontFamily: FONT.ui, letterSpacing: '0.06em', opacity: isPrimary ? 0.7 : 0.5, textTransform: 'uppercase', marginBottom: 8 }}>
            {label}
        </div>
        <div style={{ fontSize: 30, fontWeight: 800, fontFamily: FONT.mono, lineHeight: 1 }}>
            {value}
        </div>
        {sub && (
            <div style={{ fontSize: 11, fontFamily: FONT.ui, marginTop: 6, opacity: isPrimary ? 0.7 : 0.5, fontWeight: 500 }}>
                {sub}
            </div>
        )}
    </div>
);

/* ═══════════════════════════ Shift Ruler ═══════════════════════════ */
const ShiftRuler = () => {
    const now = new Date();
    const nowH = now.getHours();
    const nowM = now.getMinutes();
    const nowPct = timeToShiftPct(nowH, nowM);
    const withinShift = nowH >= SHIFT_START && nowH < SHIFT_END;

    const hours = [];
    for (let h = SHIFT_START; h <= SHIFT_END; h++) hours.push(h);

    return (
        <div style={{
            background: C.card,
            border: `1px solid ${C.border}`,
            borderRadius: 14,
            padding: '14px 22px',
            marginBottom: 20,
            position: 'relative',
        }}>
            <div style={{ display: 'flex', alignItems: 'flex-end', position: 'relative', height: 36 }}>
                {/* Hour ticks */}
                {hours.map((h, i) => {
                    const pct = (i / SHIFT_HOURS) * 100;
                    return (
                        <div key={h} style={{
                            position: 'absolute', left: `${pct}%`, transform: 'translateX(-50%)',
                            display: 'flex', flexDirection: 'column', alignItems: 'center',
                        }}>
                            <span style={{ fontSize: 9, fontFamily: FONT.mono, color: C.textMuted, fontWeight: 500 }}>
                                {h.toString().padStart(2, '0')}:00
                            </span>
                            <div style={{ width: 1, height: 10, background: C.border, marginTop: 2 }} />
                        </div>
                    );
                })}

                {/* Shift label */}
                <span style={{ position: 'absolute', left: -6, top: 0, fontSize: 9, fontFamily: FONT.ui, fontWeight: 700, color: C.textMuted, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                    SHIFT
                </span>

                {/* NOW marker */}
                {withinShift && (
                    <div style={{
                        position: 'absolute', left: `${nowPct}%`, top: -4, bottom: 0,
                        display: 'flex', flexDirection: 'column', alignItems: 'center', zIndex: 2,
                    }}>
                        <span style={{
                            fontSize: 8, fontFamily: FONT.mono, fontWeight: 700, color: C.accent,
                            background: C.accentLight, borderRadius: 4, padding: '1px 5px', marginBottom: 2,
                        }}>NOW</span>
                        <div style={{ width: 2, flex: 1, background: C.accent }} />
                    </div>
                )}
            </div>
        </div>
    );
};

/* ═══════════════════════════ Shift Bar Cell ═══════════════════════════ */
const ShiftBarCell = ({ clockIn, clockOut }) => {
    const parsed = parseTime(clockIn);
    if (!parsed) {
        // Absent
        return (
            <div style={{
                width: '100%', height: 28, background: C.absent, borderRadius: 6,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                position: 'relative',
            }}>
                <span style={{ fontSize: 10, fontFamily: FONT.ui, fontWeight: 600, color: C.textMuted, letterSpacing: '0.04em' }}>
                    ABSENT
                </span>
            </div>
        );
    }

    const isLive = !!clockIn && !clockOut;
    const now = new Date();
    const nowH = now.getHours();
    const nowM = now.getMinutes();

    const startPct = timeToShiftPct(parsed.h, parsed.m);

    let endPct;
    if (clockOut) {
        const ep = parseTime(clockOut);
        endPct = ep ? timeToShiftPct(ep.h, ep.m) : timeToShiftPct(nowH, nowM);
    } else {
        endPct = timeToShiftPct(nowH, nowM);
    }

    const barWidth = Math.max(2, endPct - startPct);

    // Compute duration
    let durationLabel = '';
    if (clockOut) {
        const ep = parseTime(clockOut);
        if (ep) {
            const totalMin = (ep.h * 60 + ep.m) - (parsed.h * 60 + parsed.m);
            const dh = Math.floor(totalMin / 60);
            const dm = totalMin % 60;
            durationLabel = dm > 0 ? `${dh}h ${dm}m` : `${dh}h`;
        }
    }

    return (
        <div style={{
            width: '100%', height: 28, background: '#f1f0ed', borderRadius: 6,
            position: 'relative', overflow: 'hidden',
        }}>
            {/* The actual shift bar */}
            <div style={{
                position: 'absolute',
                left: `${startPct}%`,
                width: `${barWidth}%`,
                top: 0, bottom: 0,
                background: isLive ? C.shiftBarLive : C.shiftBar,
                borderRadius: 6,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '0 8px',
                overflow: 'hidden',
            }}>
                {/* Animated diagonal stripes for live shifts */}
                {isLive && (
                    <div style={{
                        position: 'absolute', inset: 0,
                        backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 4px, rgba(255,255,255,0.1) 4px, rgba(255,255,255,0.1) 8px)',
                        animation: 'stripe-slide 1.5s linear infinite',
                    }} />
                )}

                <span style={{ fontSize: 9, fontFamily: FONT.mono, color: '#fff', fontWeight: 600, position: 'relative', zIndex: 1 }}>
                    {fmtTimeShort(clockIn)}
                </span>

                {durationLabel && (
                    <span style={{ fontSize: 9, fontFamily: FONT.mono, color: 'rgba(255,255,255,0.85)', fontWeight: 500, position: 'relative', zIndex: 1 }}>
                        {durationLabel}
                    </span>
                )}

                {isLive && (
                    <span style={{
                        fontSize: 8, fontFamily: FONT.ui, fontWeight: 700, color: '#fff',
                        background: 'rgba(255,255,255,0.25)', borderRadius: 4, padding: '1px 6px',
                        position: 'relative', zIndex: 1,
                    }}>
                        Live
                    </span>
                )}
            </div>
        </div>
    );
};

/* ═══════════════════════════ Attendance Table Row ═══════════════════════════ */
const AttendanceRow = ({ staff, isLast }) => {
    const cs = staff.call_stats || {};
    const isClockedIn = !!cs.clock_in_time;
    const isLive = isClockedIn && !cs.clock_out_time;
    const isAbsent = !isClockedIn;

    const attendanceParts = (staff.attendance || '0/0').split('/');
    const attPresent = Number(attendanceParts[0] || 0);
    const attTotal = Number(attendanceParts[1] || 1);
    const attPct = attTotal > 0 ? Math.round((attPresent / attTotal) * 100) : 0;

    const attBg = attPct >= 80 ? '#ecfdf5' : attPct >= 50 ? '#fffbeb' : '#fef2f2';
    const attColor = attPct >= 80 ? '#059669' : attPct >= 50 ? '#d97706' : '#dc2626';
    const attBorder = attPct >= 80 ? '#a7f3d0' : attPct >= 50 ? '#fde68a' : '#fecaca';

    const totalCalls = cs.total_calls || 0;
    const incoming = cs.incoming_calls || 0;
    const outgoing = cs.outgoing_calls || 0;
    const missed = cs.missed_calls || 0;

    return (
        <tr style={{
            borderBottom: isLast ? 'none' : `1px solid ${C.border}`,
            opacity: isAbsent ? 0.45 : 1,
            background: isLive ? '#f9fef9' : 'transparent',
            transition: 'background 0.15s',
        }}>
            {/* Staff */}
            <td style={{ padding: '14px 16px', whiteSpace: 'nowrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{
                        width: 34, height: 34, borderRadius: '50%', background: getAvatarColor(staff.name),
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: '#fff', fontWeight: 700, fontSize: 11, fontFamily: FONT.ui, flexShrink: 0,
                        position: 'relative',
                    }}>
                        {getInitials(staff.name)}
                        {isLive && (
                            <span style={{
                                position: 'absolute', bottom: -1, right: -1,
                                width: 10, height: 10, borderRadius: '50%',
                                background: C.success, border: '2px solid #fff',
                                animation: 'pulse-dot 2s infinite',
                            }} />
                        )}
                    </div>
                    <div>
                        <div style={{ fontWeight: 600, fontSize: 13, fontFamily: FONT.ui, color: C.textPrimary }}>
                            {staff.name}
                            {isLive && <span style={{ marginLeft: 6, color: C.success, fontSize: 10 }}>●</span>}
                        </div>
                        <div style={{ fontSize: 11, fontFamily: FONT.mono, color: C.textMuted }}>
                            +91 {staff.phone_number || '—'}
                        </div>
                    </div>
                </div>
            </td>

            {/* Attendance */}
            <td style={{ padding: '14px 12px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}>
                    <span style={{
                        display: 'inline-block', padding: '3px 10px', borderRadius: 8,
                        fontSize: 12, fontFamily: FONT.mono, fontWeight: 700,
                        background: attBg, color: attColor, border: `1px solid ${attBorder}`,
                    }}>
                        {staff.attendance || '0/0'}
                    </span>
                    <span style={{ fontSize: 10, fontFamily: FONT.ui, color: C.textMuted }}>
                        {attPct}% this period
                    </span>
                </div>
            </td>

            {/* Clock In */}
            <td style={{ padding: '14px 12px', fontFamily: FONT.mono, fontSize: 13, fontWeight: 600, color: C.textPrimary }}>
                {fmtTimeFull(cs.clock_in_time)}
            </td>

            {/* Clock Out */}
            <td style={{ padding: '14px 12px', fontFamily: FONT.mono, fontSize: 13, fontWeight: 600, color: C.textPrimary }}>
                {cs.clock_out_time
                    ? fmtTimeFull(cs.clock_out_time)
                    : isClockedIn
                        ? <span style={{ fontFamily: FONT.ui, fontStyle: 'italic', color: C.textMuted, fontWeight: 500, fontSize: 12 }}>in progress</span>
                        : '—'
                }
            </td>

            {/* Shift Today */}
            <td style={{ padding: '14px 12px', minWidth: 220 }}>
                <ShiftBarCell clockIn={cs.clock_in_time} clockOut={cs.clock_out_time} />
            </td>

            {/* Talk Time */}
            <td style={{ padding: '14px 12px', textAlign: 'right' }}>
                <div style={{ fontSize: 16, fontFamily: FONT.mono, fontWeight: 700, color: C.textPrimary }}>
                    {fmtTalkTime(cs.total_talk_time)}
                </div>
                <div style={{ fontSize: 9, fontFamily: FONT.ui, color: C.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    TALK TIME
                </div>
            </td>

            {/* Calls */}
            <td style={{ padding: '14px 16px', textAlign: 'right' }}>
                <div style={{ fontSize: 18, fontFamily: FONT.mono, fontWeight: 800, color: C.textPrimary }}>
                    {totalCalls}
                </div>
                <div style={{ fontSize: 9, fontFamily: FONT.mono, color: C.textMuted, fontWeight: 500 }}>
                    {totalCalls > 0
                        ? `${incoming}↗ · ${outgoing}↙ · ${missed} MISSED`
                        : 'NO CALLS'
                    }
                </div>
            </td>

            {/* Actions */}
            <td style={{ padding: '14px 8px', textAlign: 'center' }}>
                <button style={{
                    background: 'none', border: 'none', cursor: 'pointer', color: C.textMuted,
                    fontSize: 16, lineHeight: 1, padding: 4,
                }}>⋮</button>
            </td>
        </tr>
    );
};

/* ═══════════════════════════ MAIN COMPONENT ═══════════════════════════ */
export default function HrAttendanceDashboard() {

    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);
    const [dateRange, setDateRange] = useState({ start: null, end: null });
    const [searchQ, setSearchQ] = useState('');
    const [sortKey, setSortKey] = useState('attendance');
    const [sortDir, setSortDir] = useState('desc');
    const [activeTab, setActiveTab] = useState('today');
    const [nowTime, setNowTime] = useState(new Date());

    // Live clock update
    useEffect(() => {
        const timer = setInterval(() => setNowTime(new Date()), 30000);
        return () => clearInterval(timer);
    }, []);

    const fetchData = useCallback(async (sd, ed) => {
        setLoading(true);
        try {
            const res = await getHrAdminDashboard(sd, ed);
            if (res.status === 200) setData(res.data);
        } catch (err) {
            console.error(err);
            toast.error('Failed to load attendance data');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData(null, null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        const today = todayStr();
        if (tab === 'today') {
            setDateRange({ start: today, end: today });
            fetchData(today, today);
        } else if (tab === 'yesterday') {
            const y = getYesterday();
            setDateRange({ start: y, end: y });
            fetchData(y, y);
        } else if (tab === 'week') {
            const monday = getMonday(new Date());
            setDateRange({ start: monday, end: today });
            fetchData(monday, today);
        } else if (tab === 'month') {
            const mStart = getMonthStart(new Date());
            setDateRange({ start: mStart, end: today });
            fetchData(mStart, today);
        }
    };

    const handleDateRangeChange = (range) => {
        setDateRange(range);
        setActiveTab('custom');
        if (range.start && range.end) fetchData(range.start, range.end);
    };

    const handleResetDate = () => {
        setDateRange({ start: null, end: null });
        setActiveTab('today');
        fetchData(null, null);
    };

    const handleSort = (key) => {
        if (sortKey === key) {
            setSortDir(d => d === 'desc' ? 'asc' : 'desc');
        } else {
            setSortKey(key);
            setSortDir('desc');
        }
    };

    /* ─── Derived data ─── */
    const staffList = data?.staff || [];
    const totalStaff = staffList.length;
    const presentToday = staffList.filter(s => !!s.call_stats?.clock_in_time).length;
    const currentlyIn = staffList.filter(s => !!s.call_stats?.clock_in_time && !s.call_stats?.clock_out_time).length;
    const attPct = totalStaff > 0 ? Math.round((presentToday / totalStaff) * 100) : 0;

    const totalWorkMin = staffList.reduce((sum, s) => sum + workingHoursToMinutes(s.call_stats?.working_hours), 0);
    const totalTalkSec = staffList.reduce((sum, s) => sum + (s.call_stats?.total_talk_time || 0), 0);
    const totalCalls = staffList.reduce((sum, s) => sum + (s.call_stats?.total_calls || 0), 0);

    const talkPctOfWork = totalWorkMin > 0 ? Math.round((totalTalkSec / 60 / totalWorkMin) * 100) : 0;
    const avgCallsPerRep = presentToday > 0 ? Math.round(totalCalls / presentToday) : 0;

    const filteredSorted = useMemo(() => {
        return staffList
            .filter(s => {
                if (!searchQ) return true;
                const q = searchQ.toLowerCase();
                return s.name?.toLowerCase().includes(q) || s.phone_number?.includes(q);
            })
            .sort((a, b) => {
                let cmp = 0;
                if (sortKey === 'name') {
                    cmp = (a.name || '').localeCompare(b.name || '');
                } else if (sortKey === 'attendance') {
                    const aP = Number((a.attendance || '0/0').split('/')[0]);
                    const bP = Number((b.attendance || '0/0').split('/')[0]);
                    cmp = bP - aP;
                } else if (sortKey === 'clockin') {
                    const aT = a.call_stats?.clock_in_time || 'zz';
                    const bT = b.call_stats?.clock_in_time || 'zz';
                    cmp = aT.localeCompare(bT);
                } else if (sortKey === 'talktime') {
                    cmp = (b.call_stats?.total_talk_time || 0) - (a.call_stats?.total_talk_time || 0);
                } else if (sortKey === 'calls') {
                    cmp = (b.call_stats?.total_calls || 0) - (a.call_stats?.total_calls || 0);
                }
                return sortDir === 'asc' ? -cmp : cmp;
            });
    }, [staffList, searchQ, sortKey, sortDir]);

    /* ─── Styles ─── */
    const tabStyle = (key) => ({
        padding: '6px 16px',
        borderRadius: 8,
        fontSize: 13,
        fontFamily: FONT.ui,
        fontWeight: 600,
        cursor: 'pointer',
        border: 'none',
        transition: 'all 0.15s',
        background: activeTab === key ? C.accent : 'transparent',
        color: activeTab === key ? '#fff' : C.textSecondary,
    });

    const sortBtnStyle = (key) => ({
        padding: '5px 14px',
        borderRadius: 8,
        fontSize: 12,
        fontFamily: FONT.ui,
        fontWeight: 600,
        cursor: 'pointer',
        border: `1px solid ${sortKey === key ? C.accent : C.border}`,
        background: sortKey === key ? C.accent : C.card,
        color: sortKey === key ? '#fff' : C.textSecondary,
        transition: 'all 0.15s',
    });

    const colHeaderStyle = (key) => ({
        padding: '10px 12px',
        fontSize: 10,
        fontFamily: FONT.ui,
        fontWeight: 700,
        color: sortKey === key ? C.accent : C.textMuted,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        cursor: 'pointer',
        userSelect: 'none',
        whiteSpace: 'nowrap',
        borderBottom: `2px solid ${C.border}`,
    });

    const dateDisplay = data?.end_date ? fmtDate(data.end_date) : fmtDate(todayStr());
    const nowTimeStr = nowTime.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false });

    return (
        <>
            <Breadcrumbs items={[
                { name: 'Home', link: '/dashboard' },
                { name: 'Sales' },
                { name: 'HR Attendance' },
            ]} />

            {/* ─── Header ─── */}
            <div style={{ marginBottom: 24, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                <div>
                    <h1 style={{ fontSize: 28, fontWeight: 800, fontFamily: FONT.ui, color: C.textPrimary, lineHeight: 1.2, margin: 0 }}>
                        HR{' '}
                        <span style={{ fontStyle: 'italic', fontFamily: FONT.display, fontWeight: 600 }}>
                            Attendance
                        </span>
                    </h1>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 6, flexWrap: 'wrap', fontFamily: FONT.ui }}>
                        <span style={{ fontSize: 13, color: C.textSecondary }}>
                            Floor activity for <strong style={{ color: C.textPrimary }}>{dateDisplay}</strong>
                        </span>
                        <span style={{ fontSize: 12, color: C.textMuted }}>· live as of <strong style={{ fontFamily: FONT.mono, color: C.textPrimary }}>{nowTimeStr}</strong></span>
                        {currentlyIn > 0 && (
                            <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: C.accent, fontWeight: 600 }}>
                                <span style={{ width: 7, height: 7, borderRadius: '50%', background: C.success, animation: 'pulse-dot 2s infinite' }} />
                                {currentlyIn} currently in
                            </span>
                        )}
                        {loading && (
                            <span style={{ fontSize: 12, color: C.accent, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                                <svg style={{ width: 14, height: 14, animation: 'spin 1s linear infinite' }} fill="none" viewBox="0 0 24 24">
                                    <circle style={{ opacity: 0.25 }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                    <path style={{ opacity: 0.75 }} fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                                </svg>
                                Loading…
                            </span>
                        )}
                    </div>
                </div>
            </div>

            {/* ─── Date Tabs ─── */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
                <div style={{
                    display: 'flex', gap: 2, background: '#f0efec',
                    borderRadius: 10, padding: 3,
                }}>
                    <button style={tabStyle('today')} onClick={() => handleTabChange('today')}>Today</button>
                    <button style={tabStyle('yesterday')} onClick={() => handleTabChange('yesterday')}>Yesterday</button>
                    <button style={tabStyle('week')} onClick={() => handleTabChange('week')}>This week</button>
                    <button style={tabStyle('month')} onClick={() => handleTabChange('month')}>Month</button>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <DateRangePicker
                        value={dateRange}
                        onChange={handleDateRangeChange}
                        placeholder="Date Range"
                    />
                    {(dateRange.start || dateRange.end) && (
                        <button
                            onClick={handleResetDate}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textSecondary, display: 'flex', alignItems: 'center' }}
                            title="Reset"
                        >
                            <svg style={{ width: 18, height: 18 }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                        </button>
                    )}
                </div>
            </div>

            {/* ─── KPI Tiles ─── */}
            <div style={{ display: 'flex', gap: 14, marginBottom: 24, flexWrap: 'wrap' }}>
                <KpiTile
                    label="Present Today"
                    value={<>{presentToday} <span style={{ fontSize: 14, fontWeight: 500, opacity: 0.7 }}>of {totalStaff}</span></>}
                    sub={`👥 ${attPct}% attendance`}
                    isPrimary
                />
                <KpiTile
                    label="Work Hours"
                    value={minutesToDisplay(totalWorkMin)}
                    sub={`⏱ across ${presentToday} reps`}
                />
                <KpiTile
                    label="Talk Time"
                    value={fmtTalkTime(totalTalkSec)}
                    sub={`🗣 ${talkPctOfWork}% of work hours`}
                />
                <KpiTile
                    label="Total Calls"
                    value={totalCalls.toLocaleString()}
                    sub={`📞 ${avgCallsPerRep} avg per rep`}
                />
            </div>

            {/* ─── Shift Ruler ─── */}
            <ShiftRuler />

            {/* ─── Search + Sort ─── */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
                <div style={{ position: 'relative', flex: 1, minWidth: 180, maxWidth: 380 }}>
                    <svg style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', width: 15, height: 15, color: C.textMuted }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
                    </svg>
                    <input
                        type="text" placeholder="Search by name or phone…"
                        value={searchQ} onChange={e => setSearchQ(e.target.value)}
                        style={{
                            width: '100%', paddingLeft: 34, paddingRight: 12, paddingTop: 7, paddingBottom: 7,
                            fontSize: 13, fontFamily: FONT.ui, border: `1px solid ${C.border}`, borderRadius: 10,
                            background: '#fafaf8', outline: 'none',
                        }}
                    />
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 'auto' }}>
                    <span style={{ fontSize: 10, fontWeight: 700, fontFamily: FONT.ui, color: C.textMuted, letterSpacing: '0.04em' }}>SORT</span>
                    {[
                        { key: 'name', label: 'Name' },
                        { key: 'attendance', label: 'Attendance' },
                        { key: 'clockin', label: 'Clock In' },
                        { key: 'talktime', label: 'Talk Time' },
                        { key: 'calls', label: 'Calls' },
                    ].map(opt => (
                        <button key={opt.key} onClick={() => handleSort(opt.key)} style={sortBtnStyle(opt.key)}>
                            {opt.label}{sortKey === opt.key ? (sortDir === 'desc' ? ' ↓' : ' ↑') : ''}
                        </button>
                    ))}
                </div>

                <span style={{ fontSize: 12, fontFamily: FONT.ui, color: C.textMuted, fontWeight: 500 }}>
                    {filteredSorted.length} of {totalStaff} staff
                </span>
            </div>

            {/* ─── Attendance Table ─── */}
            <div style={{
                background: C.card,
                border: `1px solid ${C.border}`,
                borderRadius: 14,
                overflow: 'hidden',
                marginBottom: 24,
            }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: FONT.ui }}>
                    <thead>
                        <tr style={{ background: '#fafaf8' }}>
                            <th style={{ ...colHeaderStyle('name'), textAlign: 'left', paddingLeft: 16 }}
                                onClick={() => handleSort('name')}>
                                Staff {sortKey === 'name' ? (sortDir === 'desc' ? '↓' : '↑') : ''}
                            </th>
                            <th style={{ ...colHeaderStyle('attendance'), textAlign: 'left' }}
                                onClick={() => handleSort('attendance')}>
                                Attendance {sortKey === 'attendance' ? (sortDir === 'desc' ? '↓' : '↑') : ''}
                            </th>
                            <th style={{ ...colHeaderStyle('clockin'), textAlign: 'left' }}
                                onClick={() => handleSort('clockin')}>
                                Clock In {sortKey === 'clockin' ? (sortDir === 'desc' ? '↓' : '↑') : ''}
                            </th>
                            <th style={{ ...colHeaderStyle(''), textAlign: 'left' }}>
                                Clock Out
                            </th>
                            <th style={{ ...colHeaderStyle(''), textAlign: 'left' }}>
                                Shift Today
                            </th>
                            <th style={{ ...colHeaderStyle('talktime'), textAlign: 'right' }}
                                onClick={() => handleSort('talktime')}>
                                Talk Time {sortKey === 'talktime' ? (sortDir === 'desc' ? '↓' : '↑') : ''}
                            </th>
                            <th style={{ ...colHeaderStyle('calls'), textAlign: 'right', paddingRight: 16 }}
                                onClick={() => handleSort('calls')}>
                                Calls {sortKey === 'calls' ? (sortDir === 'desc' ? '↓' : '↑') : ''}
                            </th>
                            <th style={{ ...colHeaderStyle(''), width: 40 }}></th>
                        </tr>
                    </thead>

                    <tbody>
                        {filteredSorted.length === 0 && (
                            <tr>
                                <td colSpan={8} style={{ padding: '40px 20px', textAlign: 'center', color: C.textMuted, fontFamily: FONT.ui }}>
                                    {loading ? 'Loading attendance data…' : 'No staff data found'}
                                </td>
                            </tr>
                        )}
                        {filteredSorted.map((staff, i) => (
                            <AttendanceRow
                                key={staff.id}
                                staff={staff}
                                isLast={i === filteredSorted.length - 1}
                            />
                        ))}
                    </tbody>

                    {/* Footer totals */}
                    {filteredSorted.length > 0 && (
                        <tfoot>
                            <tr style={{ borderTop: `2px solid ${C.border}`, background: '#fafaf8' }}>
                                <td colSpan={5} style={{ padding: '12px 16px', fontSize: 12, fontFamily: FONT.ui, color: C.textMuted, fontWeight: 600 }}>
                                    {filteredSorted.length} rows · auto-refresh in 30s
                                </td>
                                <td style={{ padding: '12px 12px', textAlign: 'right' }}>
                                    <span style={{ fontSize: 10, fontFamily: FONT.ui, color: C.textMuted, fontWeight: 600, letterSpacing: '0.04em', marginRight: 4 }}>HOURS</span>
                                    <span style={{ fontSize: 14, fontFamily: FONT.mono, fontWeight: 700, color: C.textPrimary }}>{minutesToDisplay(totalWorkMin)}</span>
                                    <span style={{ fontSize: 10, fontFamily: FONT.ui, color: C.textMuted, fontWeight: 600, letterSpacing: '0.04em', marginLeft: 12, marginRight: 4 }}>TALK</span>
                                    <span style={{ fontSize: 14, fontFamily: FONT.mono, fontWeight: 700, color: C.textPrimary }}>{fmtTalkTime(totalTalkSec)}</span>
                                </td>
                                <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                                    <span style={{ fontSize: 10, fontFamily: FONT.ui, color: C.textMuted, fontWeight: 600, letterSpacing: '0.04em', marginRight: 4 }}>CALLS</span>
                                    <span style={{ fontSize: 14, fontFamily: FONT.mono, fontWeight: 700, color: C.textPrimary }}>{totalCalls}</span>
                                </td>
                                <td></td>
                            </tr>
                        </tfoot>
                    )}
                </table>
            </div>

            {/* ─── CSS Animations ─── */}
            <style>{`
                @keyframes spin { to { transform: rotate(360deg) } }
                @keyframes pulse-dot {
                    0%, 100% { opacity: 1; transform: scale(1); }
                    50% { opacity: 0.5; transform: scale(0.85); }
                }
                @keyframes stripe-slide {
                    0% { background-position: 0 0; }
                    100% { background-position: 11.31px 0; }
                }
            `}</style>
        </>
    );
}
